import React, { useEffect, useRef, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import BASE_URL from "../../../API/api";
import useBetSdk from "../../../hooks/useBetSdk";
import { getCommonParams } from "../../../config/betConfig";
import "react-toastify/dist/ReactToastify.css";
import TurboSportsHeader from "../../layouts/Header/TurboSportsHeader";

const TurboSportsPage = () => {
  const [isLaunchingGame, setIsLaunchingGame] = useState(false);
  const [iframeError, setIframeError] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const isReady = useBetSdk();
  const initializedRef = useRef(false);

  // ✅ Only launch if coming from sidebar
  const queryParams = new URLSearchParams(location.search);
  const setIframePath = queryParams.get("setIframePath");

  // ✅ Launch if coming from sidebar OR if setIframePath is present (direct navigation)
  const fromSidebar =
    location.state?.fromSidebar ||
    sessionStorage.getItem("launchTurboSports") === "true" ||
    !!setIframePath;

  useEffect(() => {
    if (!isReady || initializedRef.current || !fromSidebar) return;

    initializedRef.current = true;
    let isMounted = true;

    const launch = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please login to jump into the Game World! 🎮🚀");
        navigate("/login");
        return;
      }

      try {
        setIsLaunchingGame(true);

        const response = await axios.get(`${BASE_URL}/player/turbosports/launch`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data?.status === "success" && response.data?.game?.gameUrl) {
          const gameUrl = response.data.game.gameUrl;
          
          let sportsbookToken = null;
          try {
            const parsedUrl = new URL(gameUrl);
            sportsbookToken = parsedUrl.searchParams.get("token");
            if (!sportsbookToken && parsedUrl.hash.includes("?")) {
              const hashQuery = parsedUrl.hash.split("?")[1];
              sportsbookToken = new URLSearchParams(hashQuery).get("token");
            }
          } catch (e) {
            console.error("Error parsing gameUrl token", e);
          }

          let customStylesUri = "/assets/css/sportsbook.css";

          // ✅ fetch CSS and convert to base64 (like LiveEventsPage)
          try {
            const res = await fetch(customStylesUri);
            if (res.ok) {
              const css = await res.text();
              customStylesUri = `data:text/css;base64,${btoa(unescape(encodeURIComponent(css)))}`;
            }
          } catch (err) {
            console.error("Failed to load custom CSS:", err);
          }

          const params = getCommonParams(customStylesUri);

          // 🏗 Initialize TurboSports iframe via SDK
          const container = document.getElementById("iFrame-container");
          if (container) container.innerHTML = "";

          setTimeout(() => {
            if (window.BetSdk?.init) {
              window.BetSdk.init({
                ...params,
                token: sportsbookToken, // ✅ Pass token explicitly
                mainFrameUrl: gameUrl,
                setIframePath: setIframePath || undefined, // ✅ Pass extra path if present
                containerId: "iFrame-container",
                width: "100%",
                height: "100%",
                allowParentUrlUpdate: false,
              });
            }
          }, 200); // short delay to avoid race conditions
        } else {
          toast.error("Failed to get game URL.");
        }
      } catch (error) {
        const errMsg = error.response?.data?.message;
        if (errMsg === "Unauthenticated." || error.response?.status === 401) {
          toast.error("Please login to jump into the Game World! 🎮🚀");
          localStorage.removeItem("token");
          setTimeout(() => navigate("/login"), 3000);
        } else {
          toast.error("Game launch failed. Try again later.");
        }
      } finally {
        if (isMounted) setIsLaunchingGame(false);
      }
    };

    launch();

    return () => {
      isMounted = false;
      initializedRef.current = false;
    };
  }, [isReady, fromSidebar]);

  const handleCloseGame = () => {
    const container = document.getElementById("iFrame-container");
    if (container) container.innerHTML = "";
    navigate(-1);
  };

  return (
    <>
      <style>{`
        .tawk-min-container,
        #tawk-bubble-container,
        .wa-btn,
        .btn-telegram-pulse {
          display: none !important;
        }
      `}</style>


      {!fromSidebar && !isLaunchingGame && (
        <div style={{ padding: "20px", textAlign: "center" }}>
          Please select TurboSports from the sidebar to launch the game.
        </div>
      )}

      {isLaunchingGame && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.7)",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: "18px",
            fontWeight: "bold",
          }}
        >
          <div className="spinner-border text-light mb-3" role="status"></div>
          <p>Launching game, please wait...</p>
        </div>
      )}

      {/* Fixed Full Screen Wrapper */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100dvw",
          height: "100dvh",
          backgroundColor: "#000",
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* <StickyHeader isGameView={true} /> */}

        <TurboSportsHeader />

        {/* iFrame container for SDK */}
        <div
          id="iFrame-container"
          style={{
            flex: 1,
            width: "100%",
            backgroundColor: "#000",
          }}
        ></div>
      </div>

      {iframeError && (
        <div
          style={{
            color: "red",
            fontSize: "1.5rem",
            textAlign: "center",
            padding: "20px",
          }}
        >
          Game not visible
        </div>
      )}
    </>
  );
};

export default TurboSportsPage;